import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    type ReactNode,
} from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import type { User, DisplayUser } from "@shared-types";

const API_BASE = API_ENDPOINTS.GATEWAY;

/** Auth state and actions exposed to the app through {@link useAuth}. */
export interface AuthContextValue {
    user: User | null;
    loading: boolean;
    /** Store a freshly authenticated user in state and the session cache. */
    login: (userProfile: User) => void;
    /** Log out server-side and clear local user state. */
    logout: () => Promise<void>;
    /** Validate the session via `/auth/me`; resolves true when authenticated. */
    checkAuth: () => Promise<boolean>;
    /** Rotate the JWT via `/auth/refresh`; resolves true on success. */
    refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Build the sanitized user payload cached in sessionStorage.
 *
 * Only display-safe fields are persisted. Authorization fields (`role`,
 * `is_admin`) are deliberately NOT cached: trusting sessionStorage for
 * privilege decisions would amplify the blast radius of any XSS. Admin
 * UI must re-validate via `/auth/me` before rendering.
 */
const buildDisplayUser = (user: User | null): DisplayUser | null => {
    if (!user) return null;
    const { name, email, picture, id } = user;
    return { name: name ?? "", email, picture: picture ?? null, id };
};

interface AuthProviderProps {
    children: ReactNode;
}

interface AuthExpiredEventDetail {
    status?: number;
    message?: string;
    source?: string;
}

/**
 * Root auth provider.
 *
 * Bootstraps the session on mount, refreshes the JWT periodically and on
 * `auth:expired` events, and exposes {@link AuthContextValue} to descendants.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState<User | null>(() => {
        // Optional fast-path display from cached identity. Never trust this
        // for authorization: /auth/me is the source of truth for role.
        const cached = sessionStorage.getItem("user_cache");
        return cached ? (JSON.parse(cached) as User) : null;
    });
    const [loading, setLoading] = useState(true);

    // Function that checks authentication
    const checkAuth = useCallback(async (): Promise<boolean> => {
        try {
            const res = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
            setUser(res.data?.user || null);
            if (res.data?.user) {
                sessionStorage.setItem(
                    "user_cache",
                    JSON.stringify(buildDisplayUser(res.data.user)),
                );
                return true;
            } else {
                sessionStorage.removeItem("user_cache");
                return false;
            }
        } catch {
            setUser(null);
            sessionStorage.removeItem("user_cache");
            return false;
        }
    }, []);

    // Function that refreshes the token
    const refreshToken = useCallback(async (): Promise<boolean> => {
        try {
            const res = await axios.post(`${API_BASE}/auth/refresh`, null, { withCredentials: true });
            if (res.data?.user) {
                setUser(res.data.user);
                sessionStorage.setItem(
                    "user_cache",
                    JSON.stringify(buildDisplayUser(res.data.user)),
                );
                return true;
            }
            return false;
        } catch (err: unknown) {
            // 401 error = missing or expired refresh cookie (normal when not logged in)
            if (process.env.NODE_ENV === "development") {
                const e = err as { response?: { status?: number } };
                if (e?.response?.status === 401) {
                    console.log("[Auth] Refresh token non disponible ou expiré");
                } else {
                    console.error("[Auth] Erreur refresh token:", err);
                }
            }
            return false;
        }
    }, []);

    // Initial check on mount
    useEffect(() => {
        const run = async () => {
            await checkAuth();
            setLoading(false);
        };
        run();
    }, [checkAuth]);

    // Listen for session expiration events
    useEffect(() => {
        let refreshPromise: Promise<boolean> | null = null;

        const handleAuthExpired = async (event: Event) => {
            // Deduplicate: if a refresh is already in progress, ignore subsequent 401s
            if (refreshPromise) return;

            if (process.env.NODE_ENV === "development") {
                const detail = (event as CustomEvent<AuthExpiredEventDetail>).detail;
                console.warn("[Auth] Session expirée détectée:", detail);
            }

            // Attempt a token refresh (shared Promise to avoid concurrent calls)
            refreshPromise = refreshToken();
            const refreshed = await refreshPromise;
            refreshPromise = null;

            if (!refreshed) {
                // If the refresh fails, log the user out
                setUser(null);
                sessionStorage.removeItem("user_cache");

                globalThis.dispatchEvent(
                    new CustomEvent("auth:logout-required", {
                        detail: { reason: "Session expirée. Veuillez vous reconnecter." },
                    }),
                );
            }
        };

        globalThis.addEventListener("auth:expired", handleAuthExpired);

        return () => {
            globalThis.removeEventListener("auth:expired", handleAuthExpired);
        };
    }, [refreshToken]);

    // Refresh the token periodically (every 10 minutes)
    useEffect(() => {
        // Only start the periodic refresh when:
        // - The user is logged in
        // - The initial load is finished (avoids startup errors)
        if (!user || loading) return;

        if (process.env.NODE_ENV === "development") {
            console.log("[Auth] Démarrage du refresh périodique (10 min)");
        }

        const interval = setInterval(async () => {
            if (process.env.NODE_ENV === "development") {
                console.log("[Auth] Refresh périodique...");
            }
            const success = await refreshToken();
            if (!success) {
                if (process.env.NODE_ENV === "development") {
                    console.warn("[Auth] Refresh échoué - déconnexion");
                }
                // Log the user out if the refresh fails
                setUser(null);
                sessionStorage.removeItem("user_cache");
            }
        }, 10 * 60 * 1000); // 10 minutes

        return () => {
            if (process.env.NODE_ENV === "development") {
                console.log("[Auth] Arrêt du refresh périodique");
            }
            clearInterval(interval);
        };
    }, [user, loading, refreshToken]);

    const login = useCallback((userProfile: User) => {
        setUser(userProfile);
        sessionStorage.setItem("user_cache", JSON.stringify(buildDisplayUser(userProfile)));
    }, []);

    const logout = useCallback(async (): Promise<void> => {
        try {
            await axios.post(`${API_BASE}/auth/logout`, null, { withCredentials: true });
        } catch (err) {
            console.error("Erreur lors du logout:", err);
        } finally {
            setUser(null);
            sessionStorage.removeItem("user_cache");
        }
    }, []);

    const contextValue = useMemo<AuthContextValue>(
        () => ({ user, login, logout, loading, checkAuth, refreshToken }),
        [user, login, logout, loading, checkAuth, refreshToken],
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

/**
 * Access the auth context.
 *
 * Preserves the original JS behaviour: returns `null` when used outside a
 * provider, callers may destructure directly because <AuthProvider> wraps
 * the whole app at root. The cast keeps every existing call site working
 * without adding a null-guard everywhere; Phase 6 (strict mode) will
 * revisit this contract.
 */
export const useAuth = (): AuthContextValue => useContext(AuthContext) as AuthContextValue;

